// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import UserMessage from "./UserMessage/UserMessage";
import BotMessage from "./BotMessage/BotMessage";

// Styles
import {
  CustomTextFieldProps,
  getSurfaceTokens,
  ModalCustomConfirmDark,
} from "src/styles/mui/muiStylesHelper";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const MAX_COMMENT_LENGTH = 200;

type ChatbotModalType = {
  open: boolean;
  onClose: () => void;
};

const ChatbotModal = ({ open, onClose }: ChatbotModalType) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const { inputBorder } = getSurfaceTokens(theme);
  const showFullScreenModal = useMediaQuery(theme.breakpoints.down("sm"));

  const [commentValue, setCommentValue] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      bot: "Hello! I'm a chatbot! How are you doing today?",
      user: "",
    },
  ]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCommentValue(event.target.value);
    },
    []
  );

  const onSubmitComment = useCallback((userInput: string) => {
    setChatMessages((prev) => {
      const lastMessage = prev?.[prev?.length - 1];
      const newLastMessage = {
        ...lastMessage,
        user: userInput,
      };
      let newPrev = prev;
      newPrev[prev?.length - 1] = newLastMessage;

      // TODO: Fetch chatbot message
      const chatResponse = "Hey! This is a placeholder";
      return [...newPrev, { bot: chatResponse, user: "" }];
    });

    // Reset input value
    setCommentValue("");
  }, []);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Chatbot")}
      fullScreen={showFullScreenModal}
      maxWidth="xs"
      modalActions={
        <Grid sx={{ display: "flex", width: "100%", gap: "8px" }}>
          <TextField
            id="chatbot-input"
            data-testid="chatbot-input"
            fullWidth
            variant="outlined"
            color="warning"
            autoComplete="off"
            placeholder={t("Enter a message")}
            inputProps={{
              maxLength: MAX_COMMENT_LENGTH,
              sx: {
                "&::placeholder": {
                  color: inputBorder,
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              sx: {
                ...CustomTextFieldProps(theme),
                paddingRight: 0,
                overflowY: "hidden",
                ".MuiInputBase-input": { padding: "0px 16px" },
                ".MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #5B6D7A",
                },
              },
            }}
            value={commentValue}
            onChange={handleChange}
          />
          <Button
            disabled={commentValue?.length === 0}
            sx={ModalCustomConfirmDark}
            onClick={() => {
              onSubmitComment(commentValue);
            }}
          >
            {t("Send")}
          </Button>
        </Grid>
      }
    >
      <Grid
        display="flex"
        flexDirection="column"
        p="16px"
        gap="20px"
        sx={{
          height: "fit-content",
          minHeight: "300px",
          maxHeight: "300px",
          overflowY: "scroll",
        }}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {chatMessages.map((chatMessage, index) => (
            <Box
              key={`message-${index}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "16px",
              }}
            >
              {chatMessage.bot.length > 0 && (
                <BotMessage chatMessage={chatMessage.bot} />
              )}

              {chatMessage.user.length > 0 && (
                <UserMessage chatMessage={chatMessage.user} />
              )}
            </Box>
          ))}
        </Grid>
      </Grid>
    </DefaultModal>
  );
};

export default ChatbotModal;
